import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import klubService from '../../services/klubService';
import { ROUTES } from '../../config/routes';
import { goBackOr } from '../../utils/historyBack';
import { FORMULE_LABELS, statutSelectOptions } from '../../utils/klubUtils';
import CommercialSelect from '../../components/CommercialSelect';
import { AppButton, AppSpinner } from '../../components/ui';
import '../../styles/klub-fiche.scss';

const EMPTY_FORM = {
  entreprise: '',
  contact_nom: '',
  email: '',
  commercial_id: '',
  formule: 'total_klub',
  statut: 'en_cours',
};

function FicheSection({ icon, title, subtitle, children }) {
  return (
    <div className="kf-section">
      <div className="kf-section-head">
        <div className="kf-section-icon">
          <Icon icon={icon} />
        </div>
        <div>
          <h5 className="kf-section-title">{title}</h5>
          <p className="kf-section-sub">{subtitle}</p>
        </div>
      </div>
      <div className="kf-section-body">{children}</div>
    </div>
  );
}

const KlubAdherentCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listReturnTo = location.state?.listReturnTo || ROUTES.klub.adherents;
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedThirdparty, setSelectedThirdparty] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    klubService.listCommerciaux().then(setCommerciaux).catch(() => {});
  }, []);

  useEffect(() => {
    if (search.trim().length < 2) {
      setSearchResults([]);
      return undefined;
    }
    const t = setTimeout(() => {
      setSearching(true);
      klubService.searchDolibarrThirdparties(search)
        .then(setSearchResults)
        .catch((err) => setError(err.message))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const selectThirdparty = useCallback(async (item) => {
    setError(null);
    setLoadingPrefill(true);
    setSelectedThirdparty(item);
    try {
      const data = await klubService.getDolibarrPrefill(item.id);
      if (data?.already_linked) {
        setError('Ce client Dolibarr est déjà lié à un adhérent.');
        setSelectedThirdparty(null);
        return;
      }
      setForm({
        ...EMPTY_FORM,
        ...data.prefill,
        commercial_id: data.prefill?.commercial_id ?? '',
      });
    } catch (err) {
      setError(err.message);
      setSelectedThirdparty(null);
    } finally {
      setLoadingPrefill(false);
    }
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedThirdparty) {
      setError('Sélectionnez un client Dolibarr.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const adherent = await klubService.createAdherent({
        ...form,
        commercial_id: form.commercial_id || null,
        dolibarr_thirdparty_id: selectedThirdparty.id,
      });
      navigate(ROUTES.klub.adherentDetail(adherent.id), { state: { listReturnTo } });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterLayout>
      <div className="klub-fiche">
        <Breadcrumb title="Nouveau site Klub" hideHeading />

        <div className="kf-head">
          <div>
            <h1 className="kf-title" style={{ fontSize: 24 }}>Nouveau site Klub</h1>
            <p className="kf-section-sub mb-0">Créer une fiche à partir d&apos;un client Dolibarr</p>
          </div>
          <AppButton
            size="sm"
            variant="outline-secondary"
            icon="mdi:arrow-left"
            onClick={() => goBackOr(navigate, ROUTES.klub.dashboard)}
          >
            Retour
          </AppButton>
        </div>

        {error && <div className="kf-alert kf-alert--error">{error}</div>}

        <FicheSection
          icon="mdi:database-search-outline"
          title="Client Dolibarr"
          subtitle="Recherchez et sélectionnez le tiers à lier"
        >
          <div className="kf-field kf-col-12" style={{ maxWidth: 520 }}>
            <label className="kf-label">Recherche</label>
            <input
              className="kf-input"
              placeholder="Nom de l'entreprise…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {searching && <p className="text-secondary-light text-sm mt-2">Recherche…</p>}
          {!searching && searchResults.length > 0 && (
            <div className="list-group mt-3" style={{ maxWidth: 520 }}>
              {searchResults.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`list-group-item list-group-item-action ${selectedThirdparty?.id === item.id ? 'active' : ''}`}
                  onClick={() => selectThirdparty(item)}
                >
                  <div className="fw-medium">{item.nom}</div>
                  {item.siren && <small className="opacity-75">SIREN {item.siren}</small>}
                </button>
              ))}
            </div>
          )}
          {selectedThirdparty && (
            <div className="mt-3">
              <span className="badge bg-primary-focus text-primary-main">
                Sélectionné : {selectedThirdparty.nom}
              </span>
            </div>
          )}
        </FicheSection>

        {selectedThirdparty && (
          <FicheSection
            icon="mdi:account-plus-outline"
            title="Informations adhérent"
            subtitle="Vérifiez et complétez avant création"
          >
            {loadingPrefill ? (
              <AppSpinner />
            ) : (
              <div className="kf-grid">
                <div className="kf-field kf-col-6">
                  <label className="kf-label">Entreprise</label>
                  <input className="kf-input" value={form.entreprise} onChange={(e) => handleChange('entreprise', e.target.value)} />
                </div>
                <div className="kf-field kf-col-3">
                  <label className="kf-label">Formule</label>
                  <select className="kf-select" value={form.formule} onChange={(e) => handleChange('formule', e.target.value)}>
                    {Object.entries(FORMULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="kf-field kf-col-3">
                  <label className="kf-label">Commercial</label>
                  <CommercialSelect
                    value={form.commercial_id}
                    onChange={(next) => handleChange('commercial_id', next)}
                    commerciaux={commerciaux}
                    buttonClassName="kf-select text-start"
                  />
                </div>
                <div className="kf-field kf-col-4">
                  <label className="kf-label">Contact</label>
                  <input className="kf-input" value={form.contact_nom} onChange={(e) => handleChange('contact_nom', e.target.value)} />
                </div>
                <div className="kf-field kf-col-4">
                  <label className="kf-label">Email</label>
                  <input type="email" className="kf-input" value={form.email} onChange={(e) => handleChange('email', e.target.value)} />
                </div>
                <div className="kf-field kf-col-4">
                  <label className="kf-label">Statut</label>
                  <select className="kf-select" value={form.statut} onChange={(e) => handleChange('statut', e.target.value)}>
                    {statutSelectOptions(form.statut).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
            )}
          </FicheSection>
        )}

        {selectedThirdparty && !loadingPrefill && (
          <div className="kf-section" style={{ marginBottom: 40 }}>
            <div className="kf-actions">
              <Link to={listReturnTo} className="kf-btn kf-btn-secondary">Annuler</Link>
              <button type="button" className="kf-btn kf-btn-primary" disabled={saving} onClick={handleSubmit}>
                {saving ? 'Création…' : 'Créer l\'adhérent'}
              </button>
            </div>
          </div>
        )}
      </div>
    </MasterLayout>
  );
};

export default KlubAdherentCreatePage;
